<template>
  <v-card flat rounded="lg">
    <v-card-title class="px-6 py-3">
      <div class="subtitle1">
        {{ $t(`dashboard.securityCoverageWidgets.${widgetType}.title`) }}
      </div>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text class="py-4 px-0">
      <v-list class="py-0">
        <v-list-item
          v-for="item in activeModules"
          :key="item.name"
          class="py-1 px-6"
          @click="onModuleItemClick(item)"
        >
          <v-list-item-title class="body2">
            <div class="d-flex align-center white-space-normal">
              <v-icon
                :icon="`$${item.icon}`"
                :data-testid="`active-module-icon-${item.name}`"
                size="40"
                class="mr-4"
              ></v-icon>
              <span>
                {{ $t(`subscriptions.modules.${item.name}`) }}
              </span>
            </div>
          </v-list-item-title>

          <template #append>
            <span
              v-if="item.status === SubscriptionOverviewModuleStatus.ENABLED"
              :class="`text-${item.color} body2 pl-2`"
            >
              {{ $t("general.enabled") }}
            </span>
            <span
              v-else-if="item.status === SubscriptionOverviewModuleStatus.SETUP_REQUIRED"
              class="body2 coro-link pl-2"
            >
              {{ $t("general.setupRequired") }}
            </span>
          </template>
        </v-list-item>
        <v-divider
          v-if="activeModules.length && (disabledModules.length || addOns.length)"
        ></v-divider>
      </v-list>

      <v-expansion-panels multiple variant="accordion">
        <v-expansion-panel v-if="disabledModules.length > 0" height="56" elevation="0" hide-actions>
          <v-expansion-panel-title class="body2">
            <template #default="{ expanded }">
              <div class="d-flex align-center">
                <span class="coro-link">
                  {{
                    expanded
                      ? $t("dashboard.hideDisabledModules")
                      : $t("dashboard.seeDisabledModules")
                  }}
                </span>
                <v-icon :class="{ rotated: expanded }" icon="$chevron"></v-icon>
              </div>
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-list class="pt-0">
              <v-list-item v-for="item in disabledModules" :key="item.name" class="py-1 px-6">
                <v-list-item-title class="body2 text-indigo-medium">
                  <div class="d-flex align-center white-space-normal">
                    <v-icon
                      :icon="`$${item.icon}`"
                      :data-testid="`disabled-module-icon-${item.name}`"
                      size="40"
                      color="indigo-light"
                      class="mr-4 disabled-icon"
                    ></v-icon>
                    <span>
                      {{ $t(`subscriptions.modules.${item.name}`) }}
                    </span>
                  </div>
                </v-list-item-title>

                <template #append>
                  <span :class="`text-${item.color} body2 pl-2`">
                    {{ $t("general.disabled") }}
                  </span>
                </template>
              </v-list-item>
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>

        <v-expansion-panel
          v-if="addOns.length > 0"
          elevation="0"
          height="56"
          hide-actions
          @click="onAddonsExpandCollapse()"
        >
          <v-expansion-panel-title class="body2">
            <template #default="{ expanded }">
              <div class="d-flex align-center">
                <span class="coro-link">
                  {{ $t("dashboard.seeAddons") }}
                </span>
                <v-icon :class="{ rotated: expanded }" icon="$chevron"></v-icon>
              </div>
            </template>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-list class="py-0">
              <v-list-item
                v-for="addon in addOns"
                :key="addon.name"
                class="py-1 px-6"
                :class="{ 'not-clickable': !addon.enabled }"
                @click="onAddonItemClick(addon)"
              >
                <v-list-item-title class="body2">
                  <div
                    class="d-flex align-center white-space-normal"
                    :class="{ 'text-indigo-medium': !addon.enabled }"
                  >
                    <v-icon
                      :class="{ 'disabled-icon': !addon.enabled }"
                      :icon="`$${iconMap[addon.name]}`"
                      :data-testid="`addon-icon-${addon.name}`"
                      size="40"
                      class="mr-4"
                    ></v-icon>
                    <span>
                      {{ $t(`subscriptions.addons.${addon.name}`) }}
                    </span>
                  </div>
                </v-list-item-title>

                <template #append>
                  <span
                    class="body2 pl-2"
                    :class="addon.enabled ? 'text-green-dark' : 'text-red-dark'"
                  >
                    {{ addon.enabled ? $t("general.enabled") : $t("general.disabled") }}
                  </span>
                </template>
              </v-list-item>
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>
<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { Subscription } from "@/_store/msp/msp.module.ts";
import { SubscriptionAddon, SubscriptionModule } from "@/constants/workplaces.ts";
import { isSocAddon } from "@coronet/cui";
import { trackGtmEvent } from "@/_helpers/analytics";
import { SecurityCoverageWidgetType, WidgetType } from "@/constants/dashboard.ts";
import { useRouter } from "vue-router";
import { WIDGET_MODULE_MAP, type WidgetConfiguration, WidgetState } from "@/_helpers/dashboard.ts";
import { RouteName } from "@/constants/routes.ts";
import uniqBy from "lodash/uniqBy";
import { CoroIcons } from "@/constants/coro-icon.ts";
import type { SubscriptionAddonInfo } from "@/_store/workspace.module.ts";
import { useSettingsStore } from "@/_store/settings.module.ts";
import type { GenericCallback } from "@/types.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

export enum SubscriptionOverviewModuleStatus {
  SETUP_REQUIRED = "setupRequired",
  ENABLED = "enabled",
  DISABLED = "disabled",
}

type SubscriptionModuleUIModel = {
  name: SubscriptionModule;
  status: string;
  color: string;
  icon: string | undefined;
};

type SubscriptionModuleNavigationActions = Partial<
  Record<
    SubscriptionOverviewModuleStatus.SETUP_REQUIRED | SubscriptionOverviewModuleStatus.ENABLED,
    GenericCallback
  >
>;

const iconMap: Partial<Record<SubscriptionAddon | SubscriptionModule, string>> = {
  [SubscriptionModule.ENDPOINT_SECURITY]: CoroIcons.DEVICE_S,
  [SubscriptionModule.NETWORK]: CoroIcons.NETWORK_S,
  [SubscriptionModule.SECURITY_AWARENESS_TRAINING]: CoroIcons.SECURITY_AWARENESS_S,
  [SubscriptionModule.EMAIL_SECURITY]: CoroIcons.EMAIL_S,
  [SubscriptionModule.USER_DATA_GOVERNANCE]: CoroIcons.DATA_S,
  [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: CoroIcons.ENDPOINT_DATA_GOVERNANCE_S,
  [SubscriptionModule.MDM]: CoroIcons.MDM_SM,
  [SubscriptionModule.CLOUD_SECURITY]: CoroIcons.CLOUD_APP_S,
  [SubscriptionModule.EDR]: CoroIcons.EDR_S,
  [SubscriptionModule.SWG]: CoroIcons.DNS_ALTERNATIVE_S,
  [SubscriptionModule.BACKUP_AND_RESTORE]: CoroIcons.BACKUP_AND_RESTORE_S,

  [SubscriptionAddon.WIFI_PHISHING]: CoroIcons.WIFI_PHISHING_S,
  [SubscriptionAddon.SECURED_MESSAGES]: CoroIcons.SECURE_MESSAGE_S,
  [SubscriptionAddon.ZTNA]: CoroIcons.ZTNA_S,
  [SubscriptionAddon.INBOUND_GATEWAY]: CoroIcons.INBOUND_GATEWAY,
  [SubscriptionAddon.OUTBOUND_GATEWAY]: CoroIcons.OUTBOUND_GATEWAY,
};

export default defineComponent({
  props: {
    subscription: {
      type: Object as PropType<Subscription>,
      required: true,
    },
    setupRequiredWidgets: {
      type: Array as PropType<WidgetConfiguration<WidgetType>[]>,
      required: true,
    },
    showDisabledModules: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const widgetType = SecurityCoverageWidgetType.SUBSCRIPTION_OVERVIEW;
    const router = useRouter();

    const modulesRequiringSetup = computed(() =>
      props.setupRequiredWidgets
        .filter((w) => w.metadata.state === WidgetState.SETUP_REQUIRED)
        .map((w) => WIDGET_MODULE_MAP[w.type])
        .filter(Boolean)
    );

    const processedModules = computed<SubscriptionModuleUIModel[]>(() => {
      if (!props.subscription?.modules) return [];

      return Object.entries(props.subscription.modules).map(([moduleName, info]) => {
        const name = moduleName as SubscriptionModule;
        let status = SubscriptionOverviewModuleStatus.DISABLED;
        let color = "red-dark";

        if (info.enabled) {
          if (modulesRequiringSetup.value.includes(name)) {
            status = SubscriptionOverviewModuleStatus.SETUP_REQUIRED;
            color = "info";
          } else {
            status = SubscriptionOverviewModuleStatus.ENABLED;
            color = "green-dark";
          }
        }

        return {
          name,
          status,
          color,
          icon: iconMap[name],
        };
      });
    });

    const activeModules = computed(() =>
      processedModules.value.filter((m) => m.status !== SubscriptionOverviewModuleStatus.DISABLED)
    );

    const disabledModules = computed(() =>
      props.showDisabledModules
        ? processedModules.value.filter(
            (m) => m.status === SubscriptionOverviewModuleStatus.DISABLED
          )
        : []
    );

    // Aggregate addons from all modules into a single list for the UI panel
    const addOns = computed(() => {
      const addonArray = Object.values(props.subscription?.modules || {})
        .flatMap((moduleInfo) => moduleInfo.addons || [])
        .filter((addon) => !isSocAddon(addon.name))
        .filter((addon) => {
          if (props.showDisabledModules) {
            return true; // Show all addons
          }
          return addon.enabled;
        });
      return uniqBy(addonArray, "name");
    });

    const handleMdmLogin = async () => {
      await useSettingsStore().loginToMdm();
    };

    const createRouteAction = (routeName: RouteName): GenericCallback => {
      return async () => {
        await router.push({ name: routeName });
      };
    };

    const moduleNavigationMap: Partial<
      Record<SubscriptionModule, SubscriptionModuleNavigationActions>
    > = {
      [SubscriptionModule.CLOUD_SECURITY]: {
        [SubscriptionOverviewModuleStatus.SETUP_REQUIRED]: createRouteAction(
          RouteName.CLOUD_APPS_PAGE
        ),
        [SubscriptionOverviewModuleStatus.ENABLED]: createRouteAction(
          RouteName.CLOUD_SECURITY_PERMISSIONS_LIST_TAB
        ),
      },
      [SubscriptionModule.EMAIL_SECURITY]: {
        [SubscriptionOverviewModuleStatus.SETUP_REQUIRED]: createRouteAction(
          RouteName.CLOUD_APPS_PAGE
        ),
        [SubscriptionOverviewModuleStatus.ENABLED]: createRouteAction(
          RouteName.EMAIL_SECURITY_SETTINGS_TAB
        ),
      },
      [SubscriptionModule.USER_DATA_GOVERNANCE]: {
        [SubscriptionOverviewModuleStatus.SETUP_REQUIRED]: createRouteAction(
          RouteName.CLOUD_APPS_PAGE
        ),
        [SubscriptionOverviewModuleStatus.ENABLED]: createRouteAction(
          RouteName.USER_DATA_GOVERNANCE_MONITORING_TAB
        ),
      },
      [SubscriptionModule.SECURITY_AWARENESS_TRAINING]: {
        [SubscriptionOverviewModuleStatus.SETUP_REQUIRED]: createRouteAction(
          RouteName.PHISHING_SIMULATION
        ),
        [SubscriptionOverviewModuleStatus.ENABLED]: createRouteAction(
          RouteName.PHISHING_SIMULATION
        ),
      },
      [SubscriptionModule.ENDPOINT_SECURITY]: {
        [SubscriptionOverviewModuleStatus.SETUP_REQUIRED]: createRouteAction(
          RouteName.DEVICES_SETTINGS_AGENT_DEPLOYMENT_TAB
        ),
        [SubscriptionOverviewModuleStatus.ENABLED]: createRouteAction(
          RouteName.ENDPOINT_SECURITY_DEVICE_POSTURE_TAB
        ),
      },
      [SubscriptionModule.EDR]: {
        [SubscriptionOverviewModuleStatus.SETUP_REQUIRED]: createRouteAction(
          RouteName.DEVICES_SETTINGS_AGENT_DEPLOYMENT_TAB
        ),
        [SubscriptionOverviewModuleStatus.ENABLED]: createRouteAction(RouteName.EDR_PROCESSES_TAB),
      },
      [SubscriptionModule.ENDPOINT_DATA_GOVERNANCE]: {
        [SubscriptionOverviewModuleStatus.SETUP_REQUIRED]: createRouteAction(
          RouteName.DEVICES_SETTINGS_AGENT_DEPLOYMENT_TAB
        ),
        [SubscriptionOverviewModuleStatus.ENABLED]: createRouteAction(
          RouteName.ENDPOINT_DATA_GOVERNANCE_PRIVACY_SENSITIVE_DATA_TAB
        ),
      },
      [SubscriptionModule.NETWORK]: {
        [SubscriptionOverviewModuleStatus.ENABLED]: createRouteAction(RouteName.NETWORK_VPN_TAB),
      },
      [SubscriptionModule.SWG]: {
        [SubscriptionOverviewModuleStatus.ENABLED]: createRouteAction(
          RouteName.SECURE_WEB_GATEWAY_SETTINGS
        ),
      },
      [SubscriptionModule.MDM]: {
        [SubscriptionOverviewModuleStatus.ENABLED]: handleMdmLogin,
      },
    };

    const addonNavigationMap: Partial<Record<SubscriptionAddon, GenericCallback>> = {
      [SubscriptionAddon.INBOUND_GATEWAY]: createRouteAction(RouteName.GATEWAY_SETTINGS_INBOUND),
      [SubscriptionAddon.OUTBOUND_GATEWAY]: createRouteAction(RouteName.GATEWAY_SETTINGS_OUTBOUND),
      [SubscriptionAddon.WIFI_PHISHING]: createRouteAction(
        RouteName.ENDPOINT_SECURITY_DEVICE_ADDONS_TAB
      ),
      [SubscriptionAddon.SECURED_MESSAGES]: createRouteAction(
        RouteName.EMAIL_SECURITY_CORO_MAIL_TAB
      ),
    };

    /**
     * Handles clicks on Active Modules (both 'Enabled' and 'Setup Required')
     */
    const onModuleItemClick = async (item: SubscriptionModuleUIModel) => {
      if (
        item.status === SubscriptionOverviewModuleStatus.SETUP_REQUIRED ||
        item.status === SubscriptionOverviewModuleStatus.ENABLED
      ) {
        const module = moduleNavigationMap[item.name];

        if (!module) {
          console.error(`Unhandled module: ${item.name}`);
          return;
        }

        const action = module[item.status];
        if (!action) {
          console.error(`Unhandled ${item.status} navigation for module: ${item.name}`);
          return;
        }
        const moduleName = startCase(item.name);
        trackGtmEvent(
          GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
          `${GTMParams.SUBSCRIPTION_OVERVIEW} - ${moduleName} - ${startCase(item?.status)}`
        );

        await action();
      }
    };

    /**
     * Handles clicks on Add-ons
     */
    const onAddonItemClick = async (addon: SubscriptionAddonInfo) => {
      if (!addon.enabled) return; // Only enabled add-ons are clickable

      const action = addonNavigationMap[addon.name];
      if (!action) {
        console.error(`Unhandled navigation for add-on: ${addon.name}`);
        return;
      }

      const addonName = startCase(addon.name);
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        `${GTMParams.SUBSCRIPTION_OVERVIEW} - ${addonName}`
      );

      await action();
    };

    const onAddonsExpandCollapse = async () => {
      trackGtmEvent(
        GtmAction.WORKSPACE_VIEW_DASHBOARD_WIDGET,
        GTMParams.SUBSCRIPTION_OVERVIEW_SEE_ADDONS
      );
    };

    return {
      widgetType,
      iconMap,
      activeModules,
      disabledModules,
      addOns,
      SubscriptionOverviewModuleStatus,
      onModuleItemClick,
      onAddonsExpandCollapse,
      onAddonItemClick,
    };
  },
});
</script>

<style scoped lang="scss">
:deep(.v-expansion-panel-text) {
  .v-expansion-panel-text__wrapper {
    padding: 0;
  }

  .disabled-icon.v-icon {
    &::before {
      color: rgb(var(--v-theme-indigo-light)) !important;
    }
  }
}

:deep(.v-expansion-panel-title) {
  .v-icon.icon-chevron-down-xs {
    &::before {
      color: rgb(var(--v-theme-link)) !important;
    }
  }
}
</style>
